/**
 * React hook for MQTT integration in the DOOH Player.
 *
 * Manages the MQTT client lifecycle: connect on mount, disconnect on unmount.
 * Provides connection state, incoming messages, and command handling.
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  createMqttClient,
  type MqttClient,
  type MqttClientState,
  type ScheduleCallback,
} from './mqtt-client';
import type {
  MqttClientConfig,
  MqttConnectionState,
  PlayerCommand,
  StoredCredentials,
} from '../types';

export interface UseMqttResult {
  /** Current MQTT connection state */
  connectionState: MqttConnectionState;
  /** Detailed client state */
  clientState: MqttClientState | null;
  /** Publish a message to a topic */
  publish: (topic: string, payload: string | Uint8Array, qos?: 0 | 1 | 2) => void;
  /** Most recently received command */
  lastCommand: PlayerCommand | null;
  /** Last error that occurred */
  lastError: Error | null;
  /** Manually trigger connection */
  connect: () => Promise<void>;
  /** Manually disconnect */
  disconnect: () => void;
}

export interface UseMqttOptions {
  /** Credentials from pairing */
  credentials: StoredCredentials | null;
  /** Override MQTT config options */
  configOverrides?: Partial<MqttClientConfig>;
  /** Whether to auto-connect (default: true) */
  autoConnect?: boolean;
  /** Called when a command is received */
  onCommand?: (command: PlayerCommand) => void;
  /** Called when a schedule manifest is received (#24) */
  onSchedule?: ScheduleCallback;
  /** Called when connection state changes */
  onStateChange?: (state: MqttConnectionState) => void;
  /** Called when an error occurs */
  onError?: (error: Error) => void;
}

export function useMqtt(options: UseMqttOptions): UseMqttResult {
  const {
    credentials,
    configOverrides,
    autoConnect = true,
    onCommand,
    onSchedule,
    onStateChange,
    onError,
  } = options;

  const clientRef = useRef<MqttClient | null>(null);
  const [connectionState, setConnectionState] = useState<MqttConnectionState>('disconnected');
  const [clientState, setClientState] = useState<MqttClientState | null>(null);
  const [lastCommand, setLastCommand] = useState<PlayerCommand | null>(null);
  const [lastError, setLastError] = useState<Error | null>(null);

  // Stable refs for callbacks to avoid re-creating the client
  const onCommandRef = useRef(onCommand);
  onCommandRef.current = onCommand;
  const onScheduleRef = useRef(onSchedule);
  onScheduleRef.current = onSchedule;
  const onStateChangeRef = useRef(onStateChange);
  onStateChangeRef.current = onStateChange;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const config: MqttClientConfig | null = useMemo(() => {
    if (!credentials) return null;
    return {
      jwt: credentials.jwt,
      deviceId: credentials.deviceId,
      organizationId: credentials.organizationId,
      ...configOverrides,
    };
  }, [credentials, configOverrides]);

  // Create client when config changes
  useEffect(() => {
    // Clean up previous client
    if (clientRef.current) {
      clientRef.current.disconnect();
      clientRef.current = null;
    }

    if (!config) return;

    const client = createMqttClient(config);

    client.onStateChange((state) => {
      setConnectionState(state);
      setClientState(client.getState());
      onStateChangeRef.current?.(state);
    });

    client.onCommand((command) => {
      setLastCommand(command);
      onCommandRef.current?.(command);
    });

    client.onSchedule((manifest) => {
      onScheduleRef.current?.(manifest);
    });

    client.onError((error) => {
      setLastError(error);
      onErrorRef.current?.(error);
    });

    clientRef.current = client;

    if (autoConnect) {
      client.connect().catch((err) => {
        console.error('[useMqtt] Connection failed:', err);
        setLastError(err instanceof Error ? err : new Error(String(err)));
      });
    }

    return () => {
      client.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, autoConnect]);

  const publish = useCallback((topic: string, payload: string | Uint8Array, qos?: 0 | 1 | 2) => {
    clientRef.current?.publish(topic, payload, qos);
  }, []);

  const connect = useCallback(async () => {
    if (clientRef.current) {
      await clientRef.current.connect();
    }
  }, []);

  const disconnect = useCallback(() => {
    clientRef.current?.disconnect();
  }, []);

  return {
    connectionState,
    clientState,
    publish,
    lastCommand,
    lastError,
    connect,
    disconnect,
  };
}
